import { Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useFavorites } from '@/contexts/FavoritesContext';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface FavoriteHeartButtonProps {
  productId: string;
  size?: 'sm' | 'default';
  className?: string;
}

export function FavoriteHeartButton({ productId, size = 'sm', className }: FavoriteHeartButtonProps) {
  const { isProductFavorited, getProductFavoriteLists, addToFavorites, removeFromFavorites } = useFavorites();
  const navigate = useNavigate();
  const isFavorited = isProductFavorited(productId);
  const entries = getProductFavoriteLists(productId);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isFavorited) {
      removeFromFavorites(productId);
    } else {
      addToFavorites(productId);
    }
  };

  const iconSize = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';
  const btnSize = size === 'sm' ? 'sm' : 'default';
  const btnClass = size === 'default' ? 'h-10 w-10 p-0' : '';

  const button = (
    <Button
      variant="outline"
      size={btnSize}
      onClick={handleClick}
      className={cn(btnClass, className)}
    >
      <Heart className={cn(iconSize, isFavorited && 'fill-primary text-primary')} />
    </Button>
  );

  if (!isFavorited || entries.length === 0) return button;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="space-y-1">
            {entries.map((entry) => (
              <button
                key={entry.listId}
                className="block text-xs text-primary hover:underline cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/my-favorites/${entry.listId}`);
                }}
              >
                {entry.listName}
              </button>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
